import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import readerVersionService from '../services/readerVersionService';
import {
  Channel,
  DESCRIPTOR_SUFFIX,
  INSTALLER_MAX_BYTES,
  INSTALLER_MAX_LABEL,
  InstallerUploadResult,
  Platform,
  installerExtensions
} from '../types';

interface InstallerUploadFieldProps {
  platform: Platform;
  channel: Channel;
  uploaded: InstallerUploadResult | null;
  onUploaded: (result: InstallerUploadResult) => void;
  onUploadingChange: (uploading: boolean) => void;
}

const formatBytes = (bytes: number) => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
};

const uploadErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    // 413 do Caddy chặn trước khi tới Laravel nên không có body JSON
    if (error.response?.status === 413) return `File cài đặt vượt quá ${INSTALLER_MAX_LABEL}.`;
    if (error.response?.status === 403) return 'Chỉ tài khoản admin mới được upload file cài đặt.';
    const message = (error.response?.data as { message?: string } | undefined)?.message;
    if (message) return message;
    if (!error.response) return 'Mất kết nối tới máy chủ, vui lòng thử lại.';
  }
  return 'Upload thất bại, vui lòng thử lại.';
};

const InstallerUploadField: React.FC<InstallerUploadFieldProps> = ({
  platform,
  channel,
  uploaded,
  onUploaded,
  onUploadingChange
}) => {
  const extensions = installerExtensions(platform, channel);
  const inputRef = useRef<HTMLInputElement>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const [uploadingName, setUploadingName] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  // Đóng modal giữa chừng thì huỷ luôn request, không để upload chạy ngầm
  useEffect(() => () => controllerRef.current?.abort(), []);

  const setUploading = (name: string | null) => {
    setUploadingName(name);
    onUploadingChange(name !== null);
  };

  const handleFiles = async (chosen: File[]) => {
    setError('');

    // Script phát hành sinh hai file cạnh nhau; chọn cả hai cùng lúc rồi phân
    // loại theo đuôi, đỡ bắt người dùng bấm hai lần và ghép nhầm cặp
    const descriptor = chosen.find((f) => f.name.toLowerCase().endsWith(DESCRIPTOR_SUFFIX));
    const file = chosen.find((f) => f !== descriptor);

    if (!file) {
      setError('Chưa chọn file cài đặt.');
      return;
    }
    if (!descriptor) {
      setError(
        `Thiếu file mô tả ${DESCRIPTOR_SUFFIX}. Script phát hành sinh nó cạnh file cài — chọn cả hai cùng lúc.`
      );
      return;
    }

    // Kiểm tra trước ở client để không phải đẩy cả trăm MB lên rồi mới bị từ chối
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!extensions.includes(extension)) {
      setError(`File cài cho kênh này phải có đuôi: .${extensions.join(', .')}`);
      return;
    }
    if (file.size > INSTALLER_MAX_BYTES) {
      setError(
        `File cài đặt vượt quá ${INSTALLER_MAX_LABEL} (file đang chọn ${formatBytes(file.size)}).`
      );
      return;
    }

    const controller = new AbortController();
    controllerRef.current = controller;
    setProgress(0);
    setUploading(file.name);

    try {
      const response = await readerVersionService.uploadInstaller(
        { platform, channel, file, descriptor },
        { onProgress: setProgress, signal: controller.signal }
      );
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Upload thất bại');
      }
      onUploaded(response.data);
    } catch (err) {
      if (!axios.isCancel(err)) {
        setError(uploadErrorMessage(err));
      }
    } finally {
      controllerRef.current = null;
      setUploading(null);
    }
  };

  const pickFile = () => inputRef.current?.click();

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        multiple
        // Hộp thoại chọn file của macOS không hiểu đuôi ghép: để nguyên
        // `.release.json` thì file mô tả bị làm mờ, không chọn được. Lọc rộng
        // bằng .json, đúng đuôi hay không thì handleFiles kiểm.
        accept={[...extensions.map((ext) => `.${ext}`), '.json'].join(',')}
        className="hidden"
        onChange={(e) => {
          const chosen = Array.from(e.target.files ?? []);
          // Xoá giá trị để chọn lại đúng file vừa lỗi vẫn kích hoạt onChange
          e.target.value = '';
          if (chosen.length) handleFiles(chosen);
        }}
      />

      {uploadingName ? (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-3">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="truncate text-gray-700" title={uploadingName}>
              Đang tải lên {uploadingName}
            </span>
            <button
              type="button"
              onClick={() => controllerRef.current?.abort()}
              className="shrink-0 text-sm font-medium text-gray-600 hover:text-red-600"
            >
              Huỷ
            </button>
          </div>
          <div className="mt-2 flex items-center gap-3">
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-blue-100">
              <div
                className="h-full rounded-full bg-blue-600 transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="w-10 text-right text-xs font-medium text-blue-700">{progress}%</span>
          </div>
        </div>
      ) : uploaded ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-green-200 bg-green-50 px-3 py-3 text-sm">
          <div className="min-w-0">
            <p className="truncate font-medium text-green-800" title={uploaded.file_name}>
              ✓ {uploaded.file_name}
            </p>
            <p className="text-xs text-green-700">
              phiên bản {uploaded.version} · {formatBytes(uploaded.size)} · đã điền vào URL tải xuống
            </p>
          </div>
          <button
            type="button"
            onClick={pickFile}
            className="shrink-0 text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            Chọn file khác
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={pickFile}
          className="w-full rounded-md border-2 border-dashed border-gray-300 px-3 py-4 text-center hover:border-blue-400 hover:bg-blue-50"
        >
          <span className="block text-sm font-medium text-blue-600">
            Chọn file cài đặt và file mô tả
          </span>
          <span className="mt-0.5 block text-xs text-gray-500">
            .{extensions.join(', .')} kèm {DESCRIPTOR_SUFFIX} · tối đa {INSTALLER_MAX_LABEL}
          </span>
        </button>
      )}

      {!uploaded && !uploadingName && (
        <p className="text-xs text-gray-500">
          Chọn cả hai file cùng lúc. Phiên bản đọc từ file mô tả, không nhập tay.
        </p>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default InstallerUploadField;
